import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components';
import { useModalClose, useBodyScrollLock } from '@/common/hooks';
import {
  getGoogleWorkspaceStatus,
  getGoogleWorkspaceAuthUrl,
  getGoogleWorkspacePickerSession,
  exportGoogleWorkspaceSheet,
} from '../api';
import { GoogleSheetsIcon } from '../crm/components/GoogleSheetSelector';
import {
  loadGoogleApiScript,
  openGoogleAuthWindow,
  openSpreadsheetPicker,
  openSpreadsheetUrl,
  parseGoogleWorkspaceError,
  pickerSessionErrorMessage,
} from '../crm/utils/googleWorkspaceUtils';

import excelIcon from '../assets/excel.png';

const NATIVE_SHEET_MIME = 'application/vnd.google-apps.spreadsheet';
const GW_CALLBACK_URL = `${typeof window !== 'undefined' ? window.location.origin : ''}/integrations/google-workspace/callback`;

function resolveCompanyId(propCompanyId) {
  if (propCompanyId) return propCompanyId;
  let id =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!id) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        id = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore
    }
  }
  return id;
}

export function ContactsExportModal({
  isOpen,
  onClose,
  onExportDirect,
  isExportingDirect = false,
  selectedCount = 0,
  totalCount = 0,
  hasActiveFilters = false,
  companyId,
  activeFilters = {},
  selectedIds = [],
  excludedIds = [],
}) {
  const activeCompanyId = resolveCompanyId(companyId);

  // Export Target: 'csv' | 'xlsx' | 'google_sheets'
  const [exportTarget, setExportTarget] = useState('csv');

  // Google Sheets options
  const [sheetMode, setSheetMode] = useState('new');
  const [sheetTitle, setSheetTitle] = useState(() => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const year = now.getFullYear();
    return `WhatsApp Contacts - ${month} ${year}`;
  });
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isPickerLoading, setIsPickerLoading] = useState(false);
  const [googleExportResult, setGoogleExportResult] = useState(null);
  const [apiErrorMessage, setApiErrorMessage] = useState(null);

  useBodyScrollLock(isOpen);

  // Connection query for Google Workspace
  const {
    data: gwStatus,
    isLoading: isGwStatusLoading,
    refetch: refetchGwStatus,
  } = useQuery({
    queryKey: ['google-workspace-status', activeCompanyId],
    queryFn: () => getGoogleWorkspaceStatus(activeCompanyId),
    enabled: isOpen && !!activeCompanyId,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    const onAuthDone = (event) => {
      if (event.data?.type !== 'gw-auth-done') return;
      if (event.data.status === 'error') {
        toast.error(event.data.message || 'Google sign-in did not finish.');
        return;
      }
      if (event.data.status !== 'success') return;
      toast.success('Google Workspace connected!');
      refetchGwStatus();
    };

    const onFocus = () => {
      refetchGwStatus();
    };

    window.addEventListener('message', onAuthDone);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('message', onAuthDone);
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchGwStatus]);

  const isGwConnected = Boolean(gwStatus?.is_connected || gwStatus?.connected);

  useEffect(() => {
    if (!isOpen) {
      setExportTarget('csv');
      setSheetMode('new');
      setGoogleExportResult(null);
      setApiErrorMessage(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setGoogleExportResult(null);
    setApiErrorMessage(null);
    onClose?.();
  };

  const handleConnectGoogle = async () => {
    if (!activeCompanyId) {
      toast.error('Company ID is missing.');
      return;
    }
    setIsConnectingGoogle(true);
    setApiErrorMessage(null);
    try {
      const res = await getGoogleWorkspaceAuthUrl(
        activeCompanyId,
        GW_CALLBACK_URL,
      );
      const authUrl = res?.auth_url ?? res?.url;
      if (!authUrl) {
        toast.error('Could not retrieve Google authorization URL.');
        return;
      }
      sessionStorage.setItem('gw_company_id', String(activeCompanyId));
      localStorage.setItem('gw_company_id', String(activeCompanyId));
      openGoogleAuthWindow(authUrl);
    } catch (err) {
      const parsed = parseGoogleWorkspaceError(err);
      toast.error(parsed.message || 'Failed to initiate Google sign-in.');
    } finally {
      setIsConnectingGoogle(false);
    }
  };

  const { mutate: handleGoogleExport, isPending: isGoogleExporting } =
    useMutation({
      mutationFn: exportGoogleWorkspaceSheet,
      onSuccess: (data) => {
        setGoogleExportResult(data);
        setApiErrorMessage(null);
        if (data?.spreadsheet_url) {
          openSpreadsheetUrl(data.spreadsheet_url);
        }
        toast.success(
          `Exported ${data.row_count ?? 0} contacts to Google Sheets!`,
        );
      },
      onError: (err) => {
        const parsed = parseGoogleWorkspaceError(err);
        setApiErrorMessage(parsed.message);
        toast.error(parsed.message || 'Failed to export to Google Sheets.');
        if (parsed.isAuthError) {
          refetchGwStatus();
        }
      },
    });

  const googleExportExtras = () => {
    const payload = {
      export_type: 'whatsapp_clients',
      entity: 'whatsapp_clients',
    };
    if (activeFilters && Object.keys(activeFilters).length > 0) {
      payload.filter_params = activeFilters;
    }
    if (selectedIds && selectedIds.length > 0) {
      payload.client_ids = selectedIds;
    }
    if (excludedIds && excludedIds.length > 0) {
      payload.excluded_ids = excludedIds;
    }
    return payload;
  };

  const handleExport = () => {
    if (exportTarget === 'csv' || exportTarget === 'xlsx') {
      onExportDirect?.(exportTarget);
    } else {
      setApiErrorMessage(null);
      if (sheetMode === 'new') {
        const title = sheetTitle.trim();
        handleGoogleExport({
          companyId: activeCompanyId,
          payload: {
            ...googleExportExtras(),
            ...(title ? { title } : {}),
          },
        });
      } else {
        openExistingSheetPicker();
      }
    }
  };

  const openExistingSheetPicker = async () => {
    if (!activeCompanyId) {
      toast.error('Company ID is missing.');
      return;
    }
    const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
    if (!apiKey) {
      toast.error('VITE_GOOGLE_PICKER_API_KEY is missing.');
      return;
    }
    setIsPickerLoading(true);
    setApiErrorMessage(null);
    try {
      const session = await getGoogleWorkspacePickerSession(activeCompanyId);
      await loadGoogleApiScript();
      await openSpreadsheetPicker({
        accessToken: session.access_token,
        appId: session.app_id,
        apiKey,
        mimeTypes: NATIVE_SHEET_MIME,
        onPicked: (file) => {
          handleGoogleExport({
            companyId: activeCompanyId,
            payload: {
              spreadsheet_id: file.id,
              sheet_name: 'Sheet1',
              ...googleExportExtras(),
            },
          });
        },
      });
    } catch (err) {
      const sessionMessage = pickerSessionErrorMessage(err);
      if (sessionMessage) {
        setApiErrorMessage(sessionMessage);
        toast.error(sessionMessage);
        refetchGwStatus();
      } else {
        const parsed = parseGoogleWorkspaceError(err);
        setApiErrorMessage(parsed.message);
        toast.error(parsed.message || 'Could not open Google Picker.');
      }
    } finally {
      setIsPickerLoading(false);
    }
  };

  const modalRef = useModalClose({
    isOpen,
    onClose: handleClose,
    isDirty: false,
  });

  if (!isOpen) return null;

  const isExportActive =
    isExportingDirect || isGoogleExporting || isPickerLoading;
  const canSubmitGoogle =
    isGwConnected && !isGoogleExporting && !isPickerLoading;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200">
      <div
        ref={modalRef}
        className="contacts-export w-full max-w-[500px] bg-card-background border border-border-default rounded-2xl shadow-xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-3 duration-250 relative"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 w-7 h-7 rounded-lg text-text-muted hover:text-text-primary hover:bg-layer-7-hover flex items-center justify-center transition-colors focus:outline-none z-10"
          aria-label="Close export modal"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="pt-6 px-6 pb-2 text-center">
          <h2 className="text-[18px] sm:text-[20px] font-bold text-text-primary tracking-tight">
            Export Contacts
          </h2>
          <p className="text-[13px] text-text-secondary mt-1 font-medium">
            {selectedCount > 0
              ? `Exporting ${selectedCount} selected contact${selectedCount === 1 ? '' : 's'}`
              : hasActiveFilters
                ? `Exporting ${totalCount ?? 0} filtered contact${(totalCount ?? 0) === 1 ? '' : 's'}`
                : 'Choose a format to export your contacts'}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col gap-4">
          {/* Success State for Google Sheets */}
          {googleExportResult ? (
            <div className="p-6 bg-surface-mint/40 border border-primary/20 rounded-2xl flex flex-col items-center justify-center text-center gap-3 animate-in fade-in duration-200">
              <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-bold">
                ✓
              </div>
              <div>
                <h3 className="text-[15px] font-bold text-text-primary">
                  Export to Google Sheets Complete!
                </h3>
                <p className="text-[12px] text-text-secondary mt-0.5">
                  <strong>{googleExportResult.row_count ?? 0}</strong> contacts
                  written to{' '}
                  <span className="text-text-primary font-semibold">
                    {googleExportResult.title || 'Google Spreadsheet'}
                  </span>
                </p>
              </div>

              {googleExportResult.spreadsheet_url && (
                <button
                  type="button"
                  onClick={() =>
                    openSpreadsheetUrl(googleExportResult.spreadsheet_url)
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary-hover text-white text-[13px] font-semibold transition-all mt-1"
                >
                  <GoogleSheetsIcon className="w-4 h-4 brightness-200" />
                  <span>Open in Google Sheets</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Rectangular Option Buttons Grid (Freshsales style) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* 1. CSV File */}
                <button
                  type="button"
                  onClick={() => setExportTarget('csv')}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${
                    exportTarget === 'csv'
                      ? 'border-primary bg-surface-mint/40 ring-1 ring-primary shadow-xs'
                      : 'border-border-default bg-card-background hover:border-primary/40 hover:bg-surface-mint/15'
                  }`}
                >
                  <span className="text-[22px] leading-none shrink-0 select-none">
                    📄
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[13.5px] font-semibold text-text-primary leading-tight">
                      CSV File
                    </span>
                    <span className="text-[11.5px] text-text-secondary mt-0.5">
                      .csv spreadsheet
                    </span>
                  </div>
                  {exportTarget === 'csv' && (
                    <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] ml-auto shrink-0">
                      ✓
                    </div>
                  )}
                </button>

                {/* 2. Excel Sheet */}
                <button
                  type="button"
                  onClick={() => setExportTarget('xlsx')}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${
                    exportTarget === 'xlsx'
                      ? 'border-primary bg-surface-mint/40 ring-1 ring-primary shadow-xs'
                      : 'border-border-default bg-card-background hover:border-primary/40 hover:bg-surface-mint/15'
                  }`}
                >
                  <img
                    src={excelIcon}
                    alt="Excel"
                    className="w-6 h-6 object-contain shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[13.5px] font-semibold text-text-primary leading-tight">
                      Excel Sheet
                    </span>
                    <span className="text-[11.5px] text-text-secondary mt-0.5">
                      .xlsx workbook
                    </span>
                  </div>
                  {exportTarget === 'xlsx' && (
                    <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] ml-auto shrink-0">
                      ✓
                    </div>
                  )}
                </button>

                {/* 3. Google Sheets */}
                {isGwConnected && (
                  <button
                    type="button"
                    onClick={() => setExportTarget('google_sheets')}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${
                      exportTarget === 'google_sheets'
                        ? 'border-primary bg-surface-mint/40 ring-1 ring-primary shadow-xs'
                        : 'border-border-default bg-card-background hover:border-primary/40 hover:bg-surface-mint/15'
                    }`}
                  >
                    <GoogleSheetsIcon className="w-6 h-6 shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-[13.5px] font-semibold text-text-primary leading-tight">
                        Google Sheets
                      </span>
                      <span className="text-[11.5px] text-text-secondary mt-0.5">
                        Export directly to Google Drive
                      </span>
                    </div>
                    {exportTarget === 'google_sheets' && (
                      <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] ml-auto shrink-0">
                        ✓
                      </div>
                    )}
                  </button>
                )}
              </div>

              {/* Minimal Google Sheets Options (when active) */}
              {isGwConnected && exportTarget === 'google_sheets' && (
                <div className="flex flex-col gap-2.5 animate-in fade-in duration-200">
                  {apiErrorMessage && (
                    <div className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-[12px] text-red-700">
                      {apiErrorMessage}
                    </div>
                  )}

                  {!isGwConnected && !isGwStatusLoading ? (
                    <div className="flex items-center justify-between p-3 rounded-xl border border-border-default bg-surface-mint/20 gap-3">
                      <span className="text-[12.5px] text-text-secondary font-medium">
                        Google Workspace not connected
                      </span>
                      <button
                        type="button"
                        onClick={handleConnectGoogle}
                        disabled={isConnectingGoogle}
                        className="px-3 py-1.5 text-[12px] font-semibold text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors disabled:opacity-50 shrink-0"
                      >
                        {isConnectingGoogle
                          ? 'Connecting...'
                          : 'Connect Google'}
                      </button>
                    </div>
                  ) : isGwConnected ? (
                    <div className="flex flex-col gap-2 p-3 rounded-xl border border-border-default bg-surface-mint/15">
                      <div className="flex items-center justify-between">
                        <label className="text-[12px] font-semibold text-text-primary">
                          Spreadsheet Name
                        </label>
                        <button
                          type="button"
                          onClick={openExistingSheetPicker}
                          disabled={isPickerLoading || isGoogleExporting}
                          className="text-[11.5px] font-medium text-primary hover:underline focus:outline-none"
                        >
                          {isPickerLoading
                            ? 'Opening Drive...'
                            : 'or pick existing sheet'}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={sheetTitle}
                        onChange={(e) => setSheetTitle(e.target.value)}
                        disabled={isGoogleExporting}
                        placeholder="e.g. WhatsApp Contacts"
                        className="w-full px-3 py-2 text-[13px] bg-white border border-border-default rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all text-text-primary"
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-border-default flex items-center justify-end gap-2.5 bg-layer-1-page">
          {googleExportResult ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleClose}
              className="h-9 px-5 text-white text-[13px] font-semibold rounded-xl"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isExportActive}
                className="h-9 px-4 !bg-transparent border border-border-default hover:border-text-secondary text-text-primary text-[13px] font-medium rounded-xl transition-colors"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleExport}
                disabled={
                  isExportActive ||
                  (exportTarget === 'google_sheets' && !canSubmitGoogle)
                }
                className="h-9 px-5 text-white text-[13px] font-semibold rounded-xl transition-colors flex items-center gap-2"
              >
                {isExportActive ? (
                  <>
                    <svg
                      className="animate-spin h-3.5 w-3.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Exporting...</span>
                  </>
                ) : exportTarget === 'google_sheets' ? (
                  'Export to Sheets'
                ) : exportTarget === 'xlsx' ? (
                  'Export Excel'
                ) : (
                  'Export CSV'
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ContactsExportModal;
